import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, Popover, Alert, CircularProgress, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import httpClient, { extractErrorMessage } from '../Services/httpClient';
import LemonCoinPriceHistoryPanel from './LemonCoinPriceHistoryPanel';

const formatDateTime = (timestamp) => new Date(timestamp).toLocaleString('pt-BR');

// Painel comprimido pra 1 linha (ajuste pedido pelo usuário) — o histórico, que antes ficava
// sempre visível abaixo, virou um popover acionado pelo botão de relógio.
const LemonCoinPricePanel = () => {
    const [currentPrice, setCurrentPrice] = useState(null);
    const [lastChangeAt, setLastChangeAt] = useState(null);
    const [newPrice, setNewPrice] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [historyAnchorEl, setHistoryAnchorEl] = useState(null);

    const load = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [priceResponse, historyResponse] = await Promise.all([
                httpClient.get('/backoffice/lemon-coin-price'),
                httpClient.get('/backoffice/activity-log', {
                    params: { page: 1, pageSize: 1, entityName: 'LemonCoinPriceConfiguration' }
                })
            ]);
            setCurrentPrice(priceResponse.data);
            setNewPrice(priceResponse.data);
            setLastChangeAt(historyResponse.data.items[0]?.timestamp ?? null);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await httpClient.put('/backoffice/lemon-coin-price', Number(newPrice), {
                headers: { 'Content-Type': 'application/json' }
            });
            setSuccess('Preço atualizado com sucesso.');
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', flex: '1 1 480px' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {isLoading ? (
                <CircularProgress size={24} style={{ color: '#f77513' }} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        Cotação
                    </Typography>
                    {currentPrice !== null && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            R$ {Number(currentPrice).toFixed(4)} {lastChangeAt && `(${formatDateTime(lastChangeAt)})`}
                        </Typography>
                    )}
                    <TextField
                        label="Novo preço (BRL)"
                        type="number"
                        size="small"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        inputProps={{ step: '0.0001', min: '0' }}
                        sx={{ width: 160 }}
                    />
                    <Button
                        variant="contained"
                        disabled={isSaving}
                        style={{ backgroundColor: '#f77513', color: 'white' }}
                        onClick={handleSave}
                    >
                        Salvar
                    </Button>
                    <Tooltip title="Histórico de alterações">
                        <IconButton size="small" onClick={(e) => setHistoryAnchorEl(e.currentTarget)} sx={{ ml: 'auto', color: '#f77513' }}>
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <Popover
                open={Boolean(historyAnchorEl)}
                anchorEl={historyAnchorEl}
                onClose={() => setHistoryAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 520, maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto', p: 1 } } }}
            >
                <LemonCoinPriceHistoryPanel />
            </Popover>
        </Paper>
    );
};

export default LemonCoinPricePanel;
